import { createStore, storage } from '../lib/store';

/** How a session was authenticated: username + password, or an API key / legacy token. */
export type SessionAuthType = 'password' | 'apiKey';

/**
 * The single active session the whole app reads. With several saved
 * connections (see `connections.ts`) this mirrors the active connection.
 */
export interface AuthSession {
  serverUrl: string;
  userId: string;
  username: string;
  /** Bearer token sent on every request (JWT access token, API key, or legacy token). */
  accessToken: string;
  /** Present only on ABS 2.26+ JWT logins. */
  refreshToken?: string;
  defaultLibraryId?: string;
  serverVersion?: string;
  /** Id of the saved connection this session belongs to. */
  connectionId?: string;
  authType?: SessionAuthType;
}

export const SESSION_STORAGE_KEY = 'plume.session.v1';
/** Saved connections (`connections.ts`); each keeps its own tokens, so rotated ones show up there too. */
export const CONNECTIONS_STORAGE_KEY = 'plume.connections.v1';

export const sessionStore = createStore<AuthSession | null>(readStoredSession());

/** Set while tokens another tab stored are adopted: they are in storage already. */
let adopting = false;

sessionStore.subscribe(() => {
  if (!adopting) storage.write(SESSION_STORAGE_KEY, sessionStore.get());
});

export function getSession(): AuthSession | null {
  return sessionStore.get();
}

/** Expiry (ms since epoch) of a JWT, or null for tokens that never expire or aren't JWTs. */
export function jwtExpiry(token: string | undefined): number | null {
  const payload = token?.split('.')[1];
  if (!payload) return null;
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '='));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/** The session as last stored by any tab or window of this origin. */
export function readStoredSession(): AuthSession | null {
  const stored = storage.read<AuthSession | null>(SESSION_STORAGE_KEY, null);
  return stored && typeof stored === 'object' && typeof stored.accessToken === 'string' ? stored : null;
}

type TokenPair = Pick<AuthSession, 'accessToken' | 'refreshToken'>;

/** Token pairs other tabs stored for `current`'s account: the stored session and the saved connection. */
function storedPairsFor(current: AuthSession): TokenPair[] {
  const pairs: TokenPair[] = [];
  const stored = readStoredSession();
  if (stored && stored.serverUrl === current.serverUrl && stored.userId === current.userId && stored.accessToken) pairs.push(stored);
  const snapshot = storage.read<{ connections?: unknown } | null>(CONNECTIONS_STORAGE_KEY, null);
  const connections = Array.isArray(snapshot?.connections) ? (snapshot.connections as Array<Record<string, unknown> | null>) : [];
  for (const c of connections) {
    if (!c || typeof c.accessToken !== 'string' || !c.accessToken || c.authState === 'reauthRequired') continue;
    const sameAccount = current.connectionId ? c.id === current.connectionId : c.url === current.serverUrl && c.userId === current.userId;
    if (sameAccount) pairs.push({ accessToken: c.accessToken, refreshToken: typeof c.refreshToken === 'string' ? c.refreshToken : undefined });
  }
  return pairs;
}

/** `a` was issued after `b` (JWT expiry); unknown when either is not a JWT, which counts as newer. */
function isNewer(a: TokenPair, b: TokenPair): boolean {
  const ea = jwtExpiry(a.accessToken);
  const eb = jwtExpiry(b.accessToken);
  return ea == null || eb == null || ea > eb;
}

/**
 * Takes over tokens another tab (or the installed app's window) stored for
 * this tab's account, in the session or its saved connection: ABS 2.26+
 * rotates refresh tokens, so the old pair this tab holds would be refused on
 * its next refresh. Never steps back to an older pair (by JWT expiry). Only
 * the tokens change: an account switch or sign-out elsewhere is not followed
 * (each tab keeps its own active account until it reloads). Returns true
 * when tokens changed.
 */
export function adoptStoredTokens(): boolean {
  const current = sessionStore.get();
  if (!current) return false;
  let best: TokenPair | null = null;
  for (const pair of storedPairsFor(current)) {
    if (pair.accessToken === current.accessToken && pair.refreshToken === current.refreshToken) continue;
    if (isNewer(pair, best ?? current)) best = pair;
  }
  if (!best) return false;
  adopting = true;
  try {
    sessionStore.set({ ...current, accessToken: best.accessToken, refreshToken: best.refreshToken });
  } finally {
    adopting = false;
  }
  return true;
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === SESSION_STORAGE_KEY || event.key === CONNECTIONS_STORAGE_KEY) adoptStoredTokens();
  });
}
